import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Home, Megaphone, Package, Flame, Phone, ChevronDown, X, Smartphone, MessageCircle, Send, Share } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import api from "@/lib/api";

let deferredPrompt = null;
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); deferredPrompt = e; });
}

export default function NavDrawer() {
  const navigate = useNavigate();
  const { navOpen, setNavOpen, settings } = useStore();
  const [cats, setCats] = useState([]);
  const [prodOpen, setProdOpen] = useState(false);
  const [iosHelp, setIosHelp] = useState(false);

  useEffect(() => {
    if (navOpen) api.get("/categories").then(({ data }) => setCats(data)).catch(() => {});
  }, [navOpen]);

  const go = (path) => { setNavOpen(false); navigate(path); };

  const addToHome = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      deferredPrompt = null;
      setNavOpen(false);
    } else {
      setIosHelp(true);
    }
  };

  const NavItem = ({ icon: Icon, label, onClick, testid }) => (
    <button data-testid={testid} onClick={onClick} className="w-full flex items-center gap-4 px-6 py-4 text-left hover:bg-[#1c1c1c] transition-colors">
      <Icon size={22} className="text-[#E60000] shrink-0" />
      <span className="text-[#F0EDEA] font-heading font-semibold uppercase tracking-wide text-sm">{label}</span>
    </button>
  );

  return (
    <>
      {navOpen && <div className="fixed inset-0 bg-black/70 z-40" onClick={() => setNavOpen(false)} data-testid="nav-overlay" />}
      <aside
        className="fixed inset-y-0 left-0 w-[82vw] max-w-[340px] bg-[#111111] z-50 flex flex-col transition-transform duration-300 border-r border-[#333]"
        style={{ transform: navOpen ? "translateX(0)" : "translateX(-100%)" }}
        data-testid="nav-drawer"
      >
        <div className="h-16 flex items-center justify-between px-6 border-b border-[#333]">
          <img src="/logo_wide.webp" alt="RED EYE" className="h-8" />
          <button onClick={() => setNavOpen(false)} className="text-[#A8A2A0]" data-testid="close-nav-btn"><X size={26} /></button>
        </div>

        <nav className="flex-1 overflow-y-auto py-2">
          <NavItem icon={Home} label="Home" testid="nav-home" onClick={() => go("/")} />
          <NavItem icon={Megaphone} label="Announcements" testid="nav-announcements" onClick={() => go("/announcements")} />
          <button data-testid="nav-products-toggle" onClick={() => setProdOpen((o) => !o)} className="w-full flex items-center gap-4 px-6 py-4 text-left hover:bg-[#1c1c1c] transition-colors">
            <Package size={22} className="text-[#E60000] shrink-0" />
            <span className="text-[#F0EDEA] font-heading font-semibold uppercase tracking-wide text-sm flex-1">Products</span>
            <ChevronDown size={18} className={`text-[#A8A2A0] transition-transform ${prodOpen ? "rotate-180" : ""}`} />
          </button>
          {prodOpen && (
            <div className="bg-[#0d0d0d]">
              <button onClick={() => go("/products")} className="w-full text-left pl-16 pr-6 py-3 text-[#A8A2A0] text-sm hover:text-[#E60000]" data-testid="nav-all-products">All Products</button>
              {cats.map((c) => (
                <button key={c.id} onClick={() => go(`/products?category=${encodeURIComponent(c.name)}`)} className="w-full text-left pl-16 pr-6 py-3 text-[#A8A2A0] text-sm hover:text-[#E60000]" data-testid={`nav-cat-${c.id}`}>
                  {c.name}
                </button>
              ))}
            </div>
          )}
          <NavItem icon={Flame} label="Hot Steals" testid="nav-hotsteals" onClick={() => go("/hot-steals")} />
          <NavItem icon={Phone} label="Contact Us" testid="nav-contact" onClick={() => go("/contact")} />
        </nav>

        <div className="border-t border-[#333] p-4 space-y-2">
          <button onClick={addToHome} className="btn-skew btn-primary w-full text-xs" data-testid="add-homescreen-btn">
            <span><Smartphone size={16} /> Add to Homescreen</span>
          </button>
          <div className="flex gap-2">
            <a href={settings?.signal_link || "#"} target="_blank" rel="noreferrer" className="btn-skew btn-secondary flex-1 text-xs" data-testid="nav-signal">
              <span><MessageCircle size={16} /> Signal</span>
            </a>
            <a href={settings?.telegram_link || "#"} target="_blank" rel="noreferrer" className="btn-skew btn-secondary flex-1 text-xs" data-testid="nav-telegram">
              <span><Send size={16} /> Telegram</span>
            </a>
          </div>
        </div>
      </aside>

      {iosHelp && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-end sm:items-center justify-center p-4" onClick={() => setIosHelp(false)} data-testid="ios-install-modal">
          <div className="bg-[#1C1C1C] border border-[#333] rounded-2xl p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading font-bold uppercase text-lg text-[#F0EDEA] mb-3">Add to Home Screen</h3>
            <ol className="text-[#A8A2A0] text-sm space-y-3">
              <li className="flex gap-3 items-start"><Share size={18} className="text-[#E60000] mt-0.5 shrink-0" /> Tap the <b className="text-[#F0EDEA]">Share</b> button in your browser toolbar.</li>
              <li className="flex gap-3 items-start"><span className="text-[#E60000] font-bold">＋</span> Scroll and tap <b className="text-[#F0EDEA]">Add to Home Screen</b>.</li>
              <li className="flex gap-3 items-start"><span className="text-[#E60000] font-bold">✓</span> Tap <b className="text-[#F0EDEA]">Add</b> in the top corner.</li>
            </ol>
            <button onClick={() => setIosHelp(false)} className="btn-skew btn-primary w-full mt-6 text-sm"><span>Got it</span></button>
          </div>
        </div>
      )}
    </>
  );
}
